import { Mail, MapPin, Phone } from "lucide-react";
import { ADDRESS_LINE_1, ADDRESS_LINE_2, EMAIL, NAV_LINKS, PHONE_DISPLAY, PHONE_HREF, TEAM } from "./data";
import { Link } from "react-router-dom";
import { Wordmark, LogoMark } from "./Logo";
import { Eyebrow, MaskLines, Reveal, scrollToId } from "./motion";

const MAP_SRC = "https://www.google.com/maps?q=3705+Trindle+Road,+Camp+Hill,+PA+17011&output=embed";

export const Contact = () => (
  <footer id="contact" data-testid="contact-section" className="relative overflow-hidden pt-24 lg:pt-36">
    <div className="mx-auto max-w-[1440px] px-5 lg:px-12">
      <Eyebrow index="07">Get a quote</Eyebrow>
      <MaskLines className="mt-6 font-display text-[2.6rem] font-black uppercase leading-[0.9] tracking-[-0.04em] text-white sm:text-7xl lg:text-[7.5rem]" lines={["Your building,", <span className="text-outline">handled.</span>]} />
      <Reveal className="mt-12">
        <a href={PHONE_HREF} data-testid="footer-call-link" className="group inline-flex items-center gap-4 font-display text-4xl font-black tracking-tight text-brand-400 transition-colors duration-300 hover:text-brand-300 sm:text-6xl lg:text-7xl">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-brand-400 text-[#0A0A0A] transition-transform duration-500 group-hover:rotate-12 sm:h-20 sm:w-20">
            <Phone className="h-6 w-6 sm:h-8 sm:w-8" strokeWidth={2.5} />
          </span>
          {PHONE_DISPLAY}
        </a>
        <p className="mt-4 max-w-lg text-base text-zinc-300">Call for a site walkthrough and a scope built around your facility.</p>
      </Reveal>
      <div className="mt-16 grid gap-4 sm:grid-cols-3" data-testid="team-contacts">
        {TEAM.map((t, i) => (
          <Reveal key={t.key} delay={i * 0.08}>
            <a href={t.href} data-testid={`team-contact-${t.key}`} className="group flex h-full flex-col justify-between rounded-[22px] border border-white/10 bg-[#171717] p-7 transition-[border-color,transform,background-color] duration-500 hover:-translate-y-1 hover:border-brand-400/60 hover:bg-[#1F1F1F]">
              <div>
                <div className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-brand-400">{t.role}</div>
                <h3 className="mt-3 font-display text-2xl font-bold text-white">{t.name}</h3>
              </div>
              <div className="mt-8 flex items-center gap-3 text-lg font-semibold text-zinc-100">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-white/20 transition-[background-color,color] duration-300 group-hover:bg-brand-400 group-hover:text-black">
                  <Phone className="h-4 w-4" />
                </span>
                {t.phone}
                {t.note && <span className="text-sm font-normal text-zinc-400">({t.note.toLowerCase()})</span>}
              </div>
            </a>
          </Reveal>
        ))}
      </div>
      <div className="mt-20 grid gap-10 border-t border-white/10 pt-12 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-4">
          <a href={`mailto:${EMAIL}`} data-testid="footer-email-link" className="flex items-start gap-3 break-all text-zinc-200 transition-colors hover:text-brand-300">
            <Mail className="mt-1 h-5 w-5 shrink-0 text-brand-400" /> {EMAIL}
          </a>
          <div className="flex items-start gap-3 text-zinc-200" data-testid="footer-address">
            <MapPin className="mt-1 h-5 w-5 shrink-0 text-brand-400" />
            <span>{ADDRESS_LINE_1}<br />{ADDRESS_LINE_2}</span>
          </div>
          <nav className="flex flex-wrap gap-x-6 gap-y-3 pt-4">
            {NAV_LINKS.map((l) => (
              <button key={l.id} onClick={() => scrollToId(l.id)} data-testid={`footer-link-${l.id}`} className="nav-link text-sm text-zinc-300 hover:text-white">{l.label}</button>
            ))}
          </nav>
        </div>
        <div className="overflow-hidden rounded-[22px] border border-white/10 lg:col-span-8">
          <iframe title="Complete Maintenance Services location" src={MAP_SRC} loading="lazy" data-testid="footer-map" className="map-dark h-[320px] w-full border-0" referrerPolicy="no-referrer-when-downgrade" />
        </div>
      </div>
    </div>
    <div className="mt-20 border-t-2 border-black heather text-zinc-100" data-testid="footer-brand-band">
      <div className="mx-auto flex max-w-[1440px] flex-row items-center gap-3 px-5 py-12 sm:gap-6 lg:px-12">
        <LogoMark className="h-28 w-28 shrink-0 sm:h-44 sm:w-44 xl:h-56 xl:w-56" />
        <Wordmark className="h-28 sm:h-44 xl:h-56" knock="#3D3D3D" testId="footer-wordmark" />
      </div>
      <div className="border-t border-white/15">
        <div className="mx-auto flex max-w-[1440px] flex-col items-start justify-between gap-3 px-5 py-6 pb-28 sm:flex-row sm:items-center sm:pb-6 lg:px-12">
          <span className="text-sm text-zinc-200">© {new Date().getFullYear()} Complete Maintenance Services, Inc. · Fully insured · Camp Hill, PA</span>
          <Link to="/admin" data-testid="footer-staff-login" className="text-sm font-semibold text-zinc-200 underline underline-offset-4 hover:text-white">Staff login</Link>
        </div>
      </div>
    </div>
  </footer>
);
